/**
 * Subscription Success Page
 * Handles return from Stripe checkout and polls payment status
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      navigate('/', { replace: true });
      return;
    }

    const pollStatus = async () => {
      try {
        const result = await apiService.checkPaymentStatus(sessionId);
        
        if (result.payment_status === 'paid') {
          setStatus('success');
          // Refresh user data to get updated subscription status
          await refreshUser();
          // Redirect to home after 3 seconds
          setTimeout(() => navigate('/', { replace: true }), 3000);
        } else if (result.status === 'expired') {
          setStatus('failed');
        } else if (attempts < maxAttempts) {
          // Continue polling
          setAttempts(prev => prev + 1);
          setTimeout(pollStatus, 2000);
        } else {
          setStatus('failed');
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        if (attempts < maxAttempts) {
          setAttempts(prev => prev + 1);
          setTimeout(pollStatus, 2000);
        } else {
          setStatus('failed');
        }
      }
    };

    pollStatus();
  }, [searchParams, navigate, refreshUser, attempts]);

  return (
    <div className="min-h-screen bg-[#050810] flex items-center justify-center">
      <div className="bg-[#131b2e] rounded-2xl p-8 max-w-md w-full mx-4 text-center border border-[#1e2a45]">
        {status === 'checking' && (
          <>
            <Loader2 className="h-16 w-16 text-[#3B82F6] mx-auto mb-4 animate-spin" />
            <h2 className="text-xl font-bold text-white mb-2">Processing Payment</h2>
            <p className="text-gray-400">Please wait while we confirm your subscription...</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Subscription Activated!</h2>
            <p className="text-gray-400 mb-4">Welcome to AMP Premium! You now have full access to trading.</p>
            <p className="text-sm text-gray-500">Redirecting to home...</p>
          </>
        )}
        
        {status === 'failed' && (
          <>
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Payment Issue</h2>
            <p className="text-gray-400 mb-4">There was an issue processing your payment. Please try again.</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2 bg-[#3B82F6] text-white rounded-full hover:bg-[#2563EB] transition-colors"
            >
              Return Home
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
